import { createContext, useContext, useState, ReactNode } from 'react';
import { Pet } from '../data/types';
import { updatePet as updatePetApi, UpdatePetRequest } from '../../../api/pets';

interface PetsContextType {
  pets: Pet[];
  addPet: (pet: Pet) => void;
  updatePet: (petId: string, updates: Partial<Pet>) => Promise<void>;
  getPetById: (petId: string) => Pet | undefined;
  getAllPets: () => Pet[];
}

const PetsContext = createContext<PetsContextType | undefined>(undefined);

export function PetsProvider({ children }: { children: ReactNode }) {
  const [pets, setPets] = useState<Pet[]>([]);

  const addPet = (pet: Pet) => {
    setPets((prevPets: Pet[]) => [...prevPets, pet]);
    console.log('Pet added to context:', pet);
  };

  const updatePet = async (petId: string, updates: Partial<Pet>): Promise<void> => {
    try {
      // Extract only API-compatible fields
      const apiUpdates: Partial<UpdatePetRequest> = {};
      if (updates.name !== undefined) apiUpdates.name = updates.name;
      if (updates.species !== undefined) apiUpdates.species = updates.species as UpdatePetRequest['species'];
      if (updates.breed !== undefined) apiUpdates.breed = updates.breed;
      if (updates.dateOfBirth !== undefined) apiUpdates.dateOfBirth = updates.dateOfBirth;
      if (updates.gender !== undefined) apiUpdates.gender = updates.gender as UpdatePetRequest['gender'];
      if (updates.color !== undefined) apiUpdates.color = updates.color;
      if (updates.country !== undefined) apiUpdates.country = updates.country;
      if (updates.city !== undefined) apiUpdates.city = updates.city;
      
      // Call the API to persist the update
      await updatePetApi(petId, apiUpdates);
      // Update local state after successful API call
      setPets((prevPets: Pet[]) =>
        prevPets.map((pet: Pet) => (pet.id === petId ? { ...pet, ...updates } : pet))
      );
      console.log('Pet updated:', petId, updates);
    } catch (err) {
      console.error('Failed to update pet:', err);
      throw err;
    }
  };

  const getPetById = (petId: string) => {
    return pets.find((pet: Pet) => pet.id === petId);
  };

  const getAllPets = () => {
    return pets;
  };

  return (
    <PetsContext.Provider value={{ pets, addPet, updatePet, getPetById, getAllPets }}>
      {children}
    </PetsContext.Provider>
  );
}

export function usePets() {
  const context = useContext(PetsContext);
  if (context === undefined) {
    throw new Error('usePets must be used within a PetsProvider');
  }
  return context;
}
